import { useEffect, useMemo, useState, useCallback, useRef, FC } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import { useMonaco } from "@monaco-editor/react";
import { useTasksStore } from "@/stores/tasks";
import { useUiStore } from "@/stores/ui";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useProposalsStore } from "@/stores/proposals";
import { useReviewStore } from "@/stores/review";
import { useValidatorsStore } from "@/stores/validators";
import { http } from "@/lib/http";
import { DiffEditor } from "@/components/editor/Monaco";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  XCircle,
  Loader2,
  CheckCircle2,
  AlertCircle as AlertCircleIcon,
  Circle,
  FileCode,
  Split,
  Combine,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useSettingsStore } from "@/stores/settings";
import { RespondToProposal } from "@/components/RespondToProposal";


const HunkDiffViewer: FC<{
  proposalId: string;
  hunk: { id: string; filePath: string };
  isSideBySide: boolean;
  onEditorReady: (hunkId: string, editor: monaco.editor.IStandaloneDiffEditor | null) => void;
}> = ({ proposalId, hunk, isSideBySide, onEditorReady }) => {
  const [diffContent, setDiffContent] = useState<{
    original: string;
    modified: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { editedContent, setEditedContent, validateHunkContent, hunkValidationStatus } = useReviewStore();
  const validation = hunkValidationStatus?.[hunk.id!];

  useEffect(() => {
    const fetchContent = async () => {
      if (!hunk.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const data = await http<{ original: string; modified: string }>(
          `/api/proposals/${proposalId}/hunks/${hunk.id}/diff_content`
        );
        setDiffContent(data);
        // Pre-populate editedContent with the fetched modified content.
        // This becomes the default "final version" unless the user edits it further.
        // We check if it already exists to avoid overwriting user's pending edits
        if (!useReviewStore.getState().editedContent?.[hunk.id]) {
          setEditedContent(hunk.id, data.modified);
        }
      } catch (e: any) {
        setError(e.message || "Failed to load diff.");
        console.error("Failed to fetch diff content for hunk", hunk.id, e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [proposalId, hunk.id, setEditedContent]);

  const handleValidate = useCallback((content: string) => {
    if (hunk.id) {
      validateHunkContent(proposalId, hunk.id, hunk.filePath, content);
    }
  }, [proposalId, hunk.id, hunk.filePath, validateHunkContent]);

  if (isLoading) {
    return <div className="p-4 flex items-center justify-center h-full">Loading diff...</div>;
  }

  if (error) {
    return <div className="p-4 text-destructive flex items-center justify-center h-full">{error}</div>;
  }

  return (
    <div className="h-full no-carousel-drag">
      <DiffEditor
        filePath={hunk.filePath}
        original={diffContent?.original ?? ""}
        modified={editedContent?.[hunk.id!] ?? diffContent?.modified ?? ""}
        onModifiedChange={(value) => hunk.id && setEditedContent(hunk.id, value || "")}
        onValidate={handleValidate}
        validationStatus={validation}
        isSideBySide={isSideBySide}
        onReady={(editor) => onEditorReady(hunk.id, editor)}
      />
    </div>
  );
};

const HunkValidationIndicator = ({ hunkId, onClick }: { hunkId: string; onClick?: () => void; }) => {
  const validation = useReviewStore(s => s.hunkValidationStatus?.[hunkId]);

  if (!validation) {
    return <Circle className="h-4 w-4 text-muted-foreground" />;
  }
  if (validation.status === 'pending') {
      return <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />;
  }
  if (validation.status === 'valid') {
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  }
  if (validation.status === 'invalid') {
      return (
        <button
          type="button"
          onClick={onClick}
          className="p-0 border-0 bg-transparent cursor-pointer rounded-full focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1 focus:ring-offset-background"
          aria-label={`Scroll to syntax error: ${validation.error?.message}`}
        >
          <AlertCircleIcon className="h-4 w-4 text-destructive" />
        </button>
      );
  }
  return null;
};

export default function ReviewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const monaco = useMonaco();
  const { byId: tasksById } = useTasksStore();
  const proposalsByTaskId = useProposalsStore((s) => s.byTaskId);
  const { selections, editedContent, setHunkDecision, clearEditedContent } = useReviewStore();
  const { run: runValidators } = useValidatorsStore();
  const { setHeaderTitle, reviewTaskId: storeTaskId } = useUiStore();
  const taskId = searchParams.get("taskId") || storeTaskId;
  const urlProposalId = searchParams.get("proposalId");

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const editorRefs = useRef<Record<string, monaco.editor.IStandaloneDiffEditor | null>>({});
  const { proposeFix } = useProposalsStore();
  const { stageProposal, apply: applyProposalAction, loadProposalsForTask } = useProposalsStore();

  const handleEditorReady = useCallback((hunkId: string, editor: monaco.editor.IStandaloneDiffEditor | null) => {
    if (editor && hunkId) {
      editorRefs.current[hunkId] = editor;
    } else if (hunkId) {
      delete editorRefs.current[hunkId];
    }
  }, []);

  const isMobile = useIsMobile();
  const [isSideBySide, setIsSideBySide] = useState(true);
  const fastIteration = useSettingsStore(s => s.validators.fastIteration);
  const mergeOnSuccess = useSettingsStore((s) => s.validators.mergeOnSuccess);
  const { hunkValidationStatus } = useReviewStore();
  const task = taskId ? tasksById[taskId] : undefined;
  const proposalId = urlProposalId || task?.currentProposalId;


  useEffect(() => {
    if (taskId) {
      loadProposalsForTask(taskId);
    }
  }, [taskId, loadProposalsForTask]);

  const proposal = taskId && proposalId ? proposalsByTaskId[taskId]?.[proposalId] : undefined;

  const allHunks = useMemo(() => {
    if (!proposal?.filesChanged) return [];
    return proposal.filesChanged.flatMap((file) =>
      (file.hunks || []).map((hunk) => ({
        ...hunk,
        filePath: file.path,
        fileStatus: file.status,
      }))
    );
  }, [proposal]);

  useEffect(() => {
    setHeaderTitle('Review Changes');
  }, [setHeaderTitle]);

  const handleScrollToError = useCallback((hunkId: string) => {
    const editor = editorRefs.current[hunkId];
    const validation = useReviewStore.getState().hunkValidationStatus?.[hunkId];
    if (!editor) {
      console.warn(`Editor ref for hunk ${hunkId} not found. Cannot scroll to error.`);
      return;
    }
    if (monaco && validation?.status === 'invalid' && validation.error) {
      const modifiedEditor = editor.getModifiedEditor();
      modifiedEditor.revealLineInCenter(validation.error.line, monaco.editor.ScrollType.Smooth);
      modifiedEditor.setPosition({ lineNumber: validation.error.line, column: validation.error.column ?? 1 });
      modifiedEditor.focus();
    }
  }, [monaco]);

  const handleBack = () => {
    if (taskId) {
      navigate(`/pages/task-detail/${taskId}`);
    } else {
      navigate('/pages/tasks/');
    }
  };

    const handleProposeFix = useCallback(
      async (hunkId: string) => { // hunkId is now used to find the file
        if (!proposalId) return;
  
        const validation = hunkValidationStatus?.[hunkId];
        const hunk = allHunks.find((h) => h.id === hunkId);
        const content = editedContent?.[hunkId];
  
        if (
          !validation ||
          validation.status !== "invalid" ||
          !hunk ||
          content === undefined
        ) {
          toast.error("Could not propose fix: Missing required information.");
          return;
        }
  
        const result = await proposeFix({
          parentProposalId: proposalId,
          taskId: taskId!,
          filePath: hunk.filePath,
          content: content,
          error: validation.error as any,
        });
  
        if (result?.taskId && result.proposalId) {
          navigate(`/pages/generation-progress/?taskId=${result.taskId}&proposalId=${result.proposalId}&model=${result.model}`);
        }
      }, [proposalId, taskId, hunkValidationStatus, allHunks, editedContent, proposeFix, navigate]);


  // Default to inline view on mobile for better usability
  useEffect(() => {
    setIsSideBySide(!isMobile);
  }, [isMobile]);


  // Default all hunks to be included (applied) when the component loads
  useEffect(() => {
    if (allHunks.length > 0) {
      const unselectedHunks = allHunks.filter(h => h.id && !selections[h.id]);
      if (unselectedHunks.length > 0) {
        unselectedHunks.forEach(hunk => {
          if (hunk.id) {
            setHunkDecision(hunk.id, 'applied');
          }
        });
      }
    }
  }, [allHunks, selections, setHunkDecision]);


  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => setCurrent(api.selectedScrollSnap() + 1));
  }, [api]);


  const canFastApply = useMemo(() => {
      if (!fastIteration) return false;
      // Check if any *selected* hunk has an invalid status
      for (const hunk of allHunks) {
          if (selections[hunk.id]?.decision === 'applied') {
              const validation = hunkValidationStatus?.[hunk.id];
              // Allow applying if status is missing (not yet validated) or valid
              if (validation?.status === 'invalid') {
                  return false; // Found an invalid hunk that's applied
              }
          }
      }
      return true;
  }, [fastIteration, allHunks, selections, hunkValidationStatus]);

  const handleFastApply = async () => {
    if (!taskId || !proposalId) return;
    const toastId = toast.loading("Staging and applying changes...");
    try {
        await stageProposal(proposalId);
        await applyProposalAction(taskId, proposalId);
        toast.success("Changes applied successfully", { id: toastId });
        useUiStore.getState().setReviewTaskId(null);
        navigate(`/pages/workspace/`);
    } catch (e: any) {
        toast.error("Failed to apply changes", { id: toastId, description: e.message });
    }
  };

  const scrollAccumulator = useRef(0);
  const scrollResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SCROLL_THRESHOLD = 50;
  const SCROLL_RESET_TIMEOUT = 150;

  useEffect(() => {
    if (!api) return;

    const container = api.containerNode();
    if (!container) return;

    const handleWheel = (event: WheelEvent) => {
      if ((event.target as HTMLElement).closest('.no-carousel-drag')) {
        return;
      }

      event.preventDefault();

      if (scrollResetTimer.current) {
        clearTimeout(scrollResetTimer.current);
      }

      scrollAccumulator.current += event.deltaY;

      if (Math.abs(scrollAccumulator.current) > SCROLL_THRESHOLD) {
        if (scrollAccumulator.current > 0) {
          api.scrollNext();
        } else {
          api.scrollPrev();
        }
        scrollAccumulator.current = 0;
      } else {
        scrollResetTimer.current = setTimeout(() => {
          scrollAccumulator.current = 0;
        }, SCROLL_RESET_TIMEOUT);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (scrollResetTimer.current) {
        clearTimeout(scrollResetTimer.current);
      }
    };
  }, [api]);

  const appliedHunksCount = useMemo(() => {
    return Object.values(selections).filter((s) => s.decision === "applied").length;
  }, [selections]);

  // Keyboard shortcut for toggling view mode (Unified/Split)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "u" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSideBySide((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
  
  const handleRunValidators = useCallback(async () => {
    if (!taskId || !proposalId) return;
    await runValidators({ taskId, proposalId, scope: "impacted", applyOnSuccess: mergeOnSuccess });
    if (mergeOnSuccess) {
      toast.info("Validation started. Changes will be applied automatically on success.");
    } else {
      toast.info("Validation started. You can track progress on the task details page.");
    }
    useUiStore.getState().setReviewTaskId(null);

    navigate(`/pages/task-detail/${taskId}`);
  }, [navigate, runValidators, taskId, proposalId, mergeOnSuccess]);


  const { closeProposal } = useProposalsStore();
  const handleCloseProposal = useCallback(async () => {
    if (!taskId || !proposalId) { return; }

    await closeProposal(taskId, proposalId);
    // Clear the review task ID after the proposal is closed
    useUiStore.getState().setReviewTaskId(null);
    // After snapshot update, check the task status from the store
    const updatedTask = useTasksStore.getState().byId[taskId];
    // Clear the review task ID after the proposal is closed
    useUiStore.getState().setReviewTaskId(null);
    if (updatedTask?.status === "closed") {
      navigate("/pages/workspace/");
    } else {
      navigate("/pages/tasks/");
    }
  }, [taskId, proposalId, closeProposal, navigate]);

  if (!taskId) {
    return (
      <div className="container mx-auto p-4 text-center">
        <h1 className="text-xl font-semibold">No Review Selected</h1>
        <p className="text-muted-foreground">
          Please select a task from the Tasks page to start a review.
        </p>
        <Button className="mt-4" onClick={() => navigate('/pages/tasks/')}>Go to Tasks</Button>
      </div>
    );
  }

  if (!proposal)
    return (
      <div className="container mx-auto p-4 text-center">
        <h1 className="text-xl font-semibold">No Proposal Found</h1>
        <p className="text-muted-foreground">
          A proposal for this task is not yet available or is still generating.
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => window.history.back()}
        >
          Go Back
        </Button>
      </div>
    );
  return (
    <div className={`h-full flex flex-col ${isMobile ? 'pb-24' : 'container mx-auto p-2 md:p-4 lg:p-6 space-y-4'}`}>
      {isMobile && (
        <header className="p-2 border-b space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold truncate">{task?.title || taskId}</p>
          </div>
          {taskId && proposalId && (
              <RespondToProposal taskId={taskId} proposalId={proposalId} />
            )}
        </header>
      )}
      {!isMobile && (
        <header className="space-y-2">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold hidden md:block">Review Changes</h1>
            <p className="text-muted-foreground truncate">
              Reviewing proposal for task:{" "}
              <span className="font-semibold text-foreground">
                {task?.title || taskId}
              </span>
            </p>
          </div>
          {taskId && proposalId && (
            <div className="pt-2">
              <RespondToProposal taskId={taskId} proposalId={proposalId} variant="outline" size="sm" />
            </div>
          )}
        </div>
        <div className="space-y-1">
            {proposal.summary && (
              <p className="text-sm text-muted-foreground max-w-2xl">
                {proposal.summary}
              </p>
            )}
        </div>
        </header>
      )}
      {allHunks.length === 0 ? (
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">No Changes to Review</h2>
          <p className="mt-2 text-muted-foreground">
            This proposal does not contain any specific code hunks.
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center space-y-1 flex-1 min-h-0">
          <Carousel
            setApi={setApi}
            className={`w-full flex-1 flex flex-col ${!isMobile ? 'max-w-6xl' : ''}`}
            opts={{
              watchDrag: (_emblaApi, event) => {
                const target = event.target as HTMLElement;
                if (target.closest(".no-carousel-drag")) {
                  return false;
                }
                return true;
              },
            }}
          >
            <CarouselContent>
              {allHunks.map((hunk, index) => {
                const hunkDecision = hunk.id ? selections[hunk.id] : undefined;
                return (
                  <CarouselItem key={hunk.id || index} className="h-full">
                    <Card className={`h-full flex flex-col ${isMobile ? 'border-0 rounded-none shadow-none' : ''}`}>
                      <CardHeader className="shrink-0">
                        <div className="flex justify-between items-center">
                          <CardTitle className="flex items-center gap-2 font-mono text-base">
                            <HunkValidationIndicator hunkId={hunk.id} onClick={() => handleScrollToError(hunk.id)} />
                            <FileCode className="h-4 w-4" />
                            <span className="truncate">{hunk.filePath}</span>
                            <Badge
                              variant={
                                hunk.fileStatus === "modified"
                                  ? "secondary"
                                  : hunk.fileStatus === "added"
                                  ? "default"
                                  : "destructive"
                              }
                            >
                              {hunk.fileStatus}
                            </Badge>
                            <Badge variant="outline" className="hidden sm:inline-flex">
                              Hunk {index + 1}/{allHunks.length}
                            </Badge>
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            {hunkValidationStatus?.[hunk.id]?.status === 'invalid' && (
                                <Button variant="destructive" size="sm" className="h-7 px-2" onClick={() => handleProposeFix(hunk.id)}>
                                    <Sparkles className="mr-1 h-3 w-3" /> Propose Fix
                                </Button>
                            )}
                            {hunkDecision && (
                              <Badge
                                variant={
                                  hunkDecision.decision === "applied"
                                    ? "default"
                                    : "secondary"
                                }
                                className="capitalize"
                              >
                                {hunkDecision.decision}
                              </Badge>
                            )}
                            {isMobile && (
                                <Badge variant="outline">
                                  {current}/{count}
                                </Badge>
                            )}
                            {!isMobile && (
                              <>
                                <Button
                                  variant={isSideBySide ? "secondary" : "ghost"}
                                  size="sm"
                                  onClick={() => setIsSideBySide(true)}
                                >
                                  <Split className="mr-2 h-4 w-4" />{" "}
                                  Side-by-Side
                                </Button>
                                <Button
                                  variant={
                                    !isSideBySide ? "secondary" : "ghost"
                                  }
                                  size="sm"
                                  onClick={() => setIsSideBySide(false)}
                                >
                                  <Combine className="mr-2 h-4 w-4" /> Inline
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 min-h-0 p-0">
                        {proposalId && (
                          <HunkDiffViewer
                            proposalId={proposalId}
                            hunk={hunk}
                            isSideBySide={isSideBySide}
                            onEditorReady={handleEditorReady}
                          />
                        )}
                      </CardContent>
                      <CardFooter className="flex justify-start gap-2 bg-muted/50 shrink-0 p-4 pt-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`include-${hunk.id}`}
                            checked={hunkDecision?.decision === 'applied'}
                            onCheckedChange={(checked) => setHunkDecision(hunk.id, checked ? 'applied' : 'skipped')}
                          />
                          <Label htmlFor={`include-${hunk.id}`} className="cursor-pointer">
                            Include
                          </Label>
                        </div>
                      </CardFooter>
                    </Card>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="hidden xl:flex" />
            <CarouselNext className="hidden xl:flex" />
          </Carousel>
            {!isMobile && (
                <>
                    <div className="py-2 text-center text-sm text-muted-foreground">
                        Hunk {current} of {count}
                    </div>
                    <div className="pt-1 flex justify-center gap-2">
                        {appliedHunksCount > 0 ? (canFastApply ? (
                            <Button size="lg" onClick={handleFastApply}>
                                Apply Changes
                            </Button>
                          ) : (
                              <Button size="lg" onClick={handleRunValidators}>
                                  {mergeOnSuccess ? 'Validate & Apply' : 'Run Validators'}
                              </Button>
                          )) : ( allHunks.length > 0 &&
                            <Button size="lg" variant="destructive" onClick={handleCloseProposal}>
                                <XCircle className="mr-2 h-4 w-4" /> Close Proposal
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
      )}
      {isMobile && allHunks.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 p-4 border-t bg-background flex items-center gap-4 z-20">
              <Button variant="outline" size="lg" className="flex-1" onClick={handleBack}>Cancel</Button>
              {appliedHunksCount > 0 ? (
                <Button size="lg" className="flex-1" onClick={canFastApply ? handleFastApply : handleRunValidators}>Apply</Button>
              ) : (
                <Button size="lg" variant="destructive" className="flex-1" onClick={handleCloseProposal}>
                    <XCircle className="mr-2 h-4 w-4" /> Close Proposal
                </Button>
              )}
          </div>
      )}
    </div>
  );
}
